/* eslint-disable react-hooks/exhaustive-deps */
import 'reflect-metadata';
import React, { PropsWithChildren, useEffect, useState } from 'react';
import './App.css';
import { MainMenuShellLinksSlot, MainMenuShellLogoSlot, MainMenu, MainMenuConfig } from '@trackman/react-app-switcher';
import { ConfigurationService } from 'services/ConfigurationService';
import { useInjection } from 'services/ioc/ioc.react';
import { observer } from 'mobx-react-lite';
import { profile } from 'stores/ProfileStore';
import { AuthenticationService } from 'services/AuthenticationService';
import { Link } from 'react-router-dom';
import { ServiceTypes } from './constants/ServiceTypes';
import { AppPermissions } from './constants/AppPermissions';
import Forbidden from './pages/Forbidden/Forbidden';
import { Cannot, Can } from './components/Can';
import 'react-toastify/dist/ReactToastify.min.css';
import Loader from 'components/loader/Loader';
import profilePopupBackgroundUrl from './images/MainMenu/menu-background-default.png';
import defaultProfilePhotoUrl from './images/MainMenu/unknown-user.png';
import { UiStore } from 'stores/UiStore';
import { ToastContainer } from 'react-toastify';
import ErrorBoundary from 'components/error/ErrorBundary';
import Snackbar from 'components/Snackbar/Snackbar';
import { SessionLockFeedService } from 'services/data-services/SessionLockFeedService';
import { SessionVerificationStore } from 'stores/SessionVerificationStore';
import { ISessionLockMessage } from 'interfaces/session/ISessionLock';
import { BuildInfoStore } from 'stores/BuildInfoStore';

interface IProps {
  initialConfig?: MainMenuConfig;
}

const App: React.FC<PropsWithChildren<IProps>> = observer((props) => {
  const uiStore = useInjection<UiStore>(ServiceTypes.UiStore);
  const authenticationService = useInjection<AuthenticationService>(ServiceTypes.AuthenticationService);
  const configService = useInjection<ConfigurationService>(ServiceTypes.ConfigurationService);
  const [config, setConfig] = useState<MainMenuConfig>({} as MainMenuConfig);
  const [isInit, setInit] = useState<boolean>(false);
  const [accountLngUrl, setAccountLngUrl] = useState<string>('');
  const [logoutUrl, setLogoutUrl] = useState<string>('');
  const sessionVerificationStore = useInjection<SessionVerificationStore>(ServiceTypes.SessionVerificationStore);
  const buildInfoStore = useInjection<BuildInfoStore>(ServiceTypes.BuildInfoStore);
  const buildNumber = buildInfoStore.buildInfo?.buildNumber;

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (!isInit) {
      return;
    }
    const authData = authenticationService.getAuthData();
    setConfig({
      accessToken: authData ? authData.accessToken : '',
      accountBaseUrl: profile.authority,
      defaultProfilePhotoUrl,
      profilePopupBackgroundUrl,
      profilePopupTexts: {
        logoutText: 'Log Out',
        myProfileText: 'My Profile',
        supportAndFaqText: 'Support and FAQ',
      },
      onGotoProfile: () => {
        window.location.assign(accountLngUrl);
      },
      onLogout: (event: { preventDefault: () => void }) => {
        event.preventDefault();
        window.location.assign(logoutUrl + '?returnUrl=' + window.origin);
      },
    } as MainMenuConfig);
  }, [isInit]);

  const init = async () => {
    await authenticationService.initialize();
    const [configData] = await Promise.all([
      profile.ensureDataAvailability().then(() => configService.getConfigs()),
      buildInfoStore.initialize(),
    ]);
    setLogoutUrl(configData.logoutUrl);
    setAccountLngUrl(configData.myAccountUrl);
    setInit(true);
  };

  const links = (
    <MainMenuShellLinksSlot translate='no'>
      <Link to='/'>
        <span>Sessions</span>
      </Link>
    </MainMenuShellLinksSlot>
  );

  const logo = (
    <MainMenuShellLogoSlot translate='no'>
      <img src={'/images/tmlogo.svg'} alt='trackman' />
    </MainMenuShellLogoSlot>
  );

  return (
    <ErrorBoundary>
      <div style={{ minHeight: '100vh' }}>
        <ToastContainer autoClose={10000} progressClassName='toast-progress-bar' />
        <div className='app'>
          {config.accessToken ? (
            <>
              <div className={`main-menu${uiStore.isNavbarVisible ? ' nav-bar-visible' : ''}`}>
                <MainMenu
                  config={config}
                  links={links}
                  logo={logo}
                  translate='no'
                  profileIconFooterText={buildNumber ? `Build number: ${buildNumber}` : undefined}
                />
              </div>
              <Snackbar />
              <Can need={AppPermissions.PortalAccess}>{props.children}</Can>
              <Cannot need={AppPermissions.PortalAccess}>
                <Forbidden />
              </Cannot>
            </>
          ) : (
            <div
              style={{
                left: '50%',
                top: '50%',
                transform: 'translate(-50%,-50%)',
                position: 'absolute',
              }}
            >
              <Loader size='large' />
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
});

export default App;
